import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import swal from "sweetalert";
import "../../components/CustomComponents/footer/footer.css";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import {
  mergeVisibleColumns,
  needsPreferencesUpdate,
} from "../../utils/preferencesUtils";
import ItemAssignmentModal from "../../components/CustomComponents/modal/ItemAssignmentModal";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import { applyCatalogTotalFromFetch } from "../../utils/catalogTotalRecords";



const MODIFIER_GROUP_SORT_FIELDS = {
  name: "Name",
  price: "Price",
};

const MODIFIER_GROUP_SORT_LABELS = {
  [MODIFIER_GROUP_SORT_FIELDS.name]: "Name",
  [MODIFIER_GROUP_SORT_FIELDS.price]: "Price",
};

function ManageModifierGroups() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [modifierGroups, setModifierGroups] = useState([]);
  const [skipConfirmModifier, setSkipConfirmModifier] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const lastSelectedIndex = useRef(null);
  const [showItemAssignmentModal, setShowItemAssignmentModal] = useState(false);
  const [
    selectedModifierGroupForAssignment,
    setSelectedModifierGroupForAssignment,
  ] = useState(null);
  const [showBulkItemAssignmentModal, setShowBulkItemAssignmentModal] =
    useState(false);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const SHOW_BULK_ACTIONS = false;

  const pageKey = "modifierGroups";
  const defaultVisibleColumns = {
    name: true,
    subTitle: true,
    minSelection: true,
    maxSelection: true,
    freeSelection: true,
    canSkip: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Name", label: "Name" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];
  const preferences = useSelector(
    (state) => state.itemGridPreferences[pageKey] || {}
  );
  const visibleColumns = mergeVisibleColumns(
    preferences.visibleColumns,
    defaultVisibleColumns
  );
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();

  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const modifierGroupsFetchSeqRef = useRef(0);

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const hasFiltersApplied = () => search.trim() !== "";

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: MODIFIER_GROUP_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  const handleDelete = async (modifier) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${modifier.name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await axiosInstance
        .delete(APIs.DELETE_MODIFIER_GROUP, {
          params: {
            CompID,
            ModifierGroupID: modifier.modifierGroupID,
          },
        })
        .then((response) => {
          if (response.data.status) {
            setModifierGroups((prevMenu) =>
              prevMenu.filter(
                (item) => item.modifierGroupID !== modifier.modifierGroupID
              )
            );
            showSuccessToast("Modifier Group is Deleted.");
          } else {
            showErrorToast(response.data.message || "Deletion failed");
          }
        });
    } catch (err) {
      showErrorToast("Something Went Wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (modifierGroupID, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (modifierGroups[i]?.modifierGroupID) {
            newSelected.add(modifierGroups[i].modifierGroupID);
          }
        }
      } else {
        if (newSelected.has(modifierGroupID)) {
          newSelected.delete(modifierGroupID);
        } else {
          newSelected.add(modifierGroupID);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(
        modifierGroups.map((item) => item.modifierGroupID)
      );
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No modifier groups selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected modifier groups?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((modifierGroupID) =>
          axiosInstance.delete(APIs.DELETE_MODIFIER_GROUP, {
            params: {
              CompID,
              ModifierGroupID: modifierGroupID,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchModifierGroupsData();
      showSuccessToast(
        `${selectedItems.size} modifier groups deleted successfully.`
      );
    } catch (err) {
      // Error in bulk delete silently
      showErrorToast("Failed to delete selected modifier groups.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No modifier groups selected for editing.");
      return;
    }

    const selectedModifierGroupIds = Array.from(selectedItems);
    navigate(`/modifiers/bulkEdit`, { state: { selectedModifierGroupIds } });
  };

  const handleBulkStatusChange = async (canSkipStatus) => {
    if (selectedItems.size === 0) {
      showErrorToast("No modifier groups selected for status change.");
      return;
    }

    try {
      const confirmStatusChange = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to ${
          canSkipStatus ? "enable" : "disable"
        } skip for ${selectedItems.size} selected modifier groups?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmStatusChange) return;

      setLoading(true);
      const updatedModifierGroups = modifierGroups.map((group) =>
        selectedItems.has(group.modifierGroupID)
          ? { ...group, canSkip: canSkipStatus }
          : group
      );
      setModifierGroups(updatedModifierGroups);

      await Promise.all(
        Array.from(selectedItems).map((modifierGroupID) =>
          axiosInstance.put(APIs.POST_CHANGE_SKIP_ABLE, {
            CompID,
            ModifierGroupID: modifierGroupID,
            canSkip: canSkipStatus,
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      showSuccessToast(
        `${selectedItems.size} modifier groups status updated successfully.`
      );
    } catch (error) {
      // Error in bulk status change silently
      showErrorToast("Failed to update selected modifier groups status.");
      fetchModifierGroupsData();
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    } finally {
      setLoading(false);
    }
  };

  const confirmSkipChange = async () => {
    if (!skipConfirmModifier) return;

    const { modifierGroupID, canSkip, modifierGroupName, name } = skipConfirmModifier;
    const newCanSkip = !canSkip;
    const displayName = modifierGroupName ?? name ?? "Modifier group";

    setModifierGroups((prevGroups) =>
      prevGroups.map((group) =>
        group.modifierGroupID === modifierGroupID
          ? { ...group, canSkip: newCanSkip }
          : group
      )
    );
    setSkipConfirmModifier(null);

    try {
      setLoading(true);
      await axiosInstance.put(APIs.POST_CHANGE_SKIP_ABLE, {
        CompID,
        ModifierGroupID: modifierGroupID,
        canSkip: newCanSkip,
      });
      showSuccessToast(
        `"${displayName}" can skip is now ${newCanSkip ? "Yes" : "No"}.`
      );
    } catch (err) {
      setModifierGroups((prevGroups) =>
        prevGroups.map((group) =>
          group.modifierGroupID === modifierGroupID
            ? { ...group, canSkip }
            : group
        )
      );
      showErrorToast("Failed to update modifier group skip setting.");
    } finally {
      setLoading(false);
    }
  };

  const fetchModifierGroupsData = useCallback(async () => {
    const fetchSeq = ++modifierGroupsFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_MODIFIER_GROUPS, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });

      if (fetchSeq !== modifierGroupsFetchSeqRef.current) return;

      const MGroupsData = response.data.data || response.data;
      if (Array.isArray(MGroupsData)) {
        setModifierGroups(MGroupsData);
        if (!search.trim()) {
          applyCatalogTotalFromFetch(setCatalogTotalRecords, MGroupsData.length);
        }
      } else {
        setModifierGroups([]);
      }
    } catch (err) {
      if (fetchSeq !== modifierGroupsFetchSeqRef.current) return;
      setModifierGroups([]);
      showErrorToast("Failed to fetch modifier groups.");
    } finally {
      if (fetchSeq === modifierGroupsFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, sortBy, sortOrder, CompID]);

  const totalRecords = modifierGroups.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedModifierGroups = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return modifierGroups.slice(start, start + size);
  }, [modifierGroups, pageNumber, pageSize]);

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (
        needsPreferencesUpdate(
          preferences.visibleColumns,
          defaultVisibleColumns
        )
      ) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(
          preferences.visibleColumns,
          defaultVisibleColumns
        );
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(modifierGroupsFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchModifierGroupsData(), [fetchModifierGroupsData]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No modifier groups match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no modifier groups to show right now. Add a new group or check back later.";
  };

  const renderNoDataMessage = () => {
    if (loading || modifierGroups.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No modifier groups found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  // Compute selected groups for button visibility
  const selectedGroups = modifierGroups.filter(group => selectedItems.has(group.modifierGroupID));
  const anyEnabled = selectedGroups.some(group => group.canSkip);
  const anyDisabled = selectedGroups.some(group => !group.canSkip);

  return (
    <div>
<div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Modifier Groups</li>
          </ol>
          <h1 className="page-header mb-0">Manage Modifier Groups</h1>
        </div>
        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={() => {
              navigate("/modifiers/addModifierGroup");
            }}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Modifier Group
          </button>
        </div>
      </div>
      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton
            onClick={() => navigate("/modifiers/addModifierGroup")}
            title="Add Modifier Group"
          />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Modifier Groups"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && pagedModifierGroups.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          {visibleColumns.name && (
                            <SortableColumnHeader
                              sortField={MODIFIER_GROUP_SORT_FIELDS.name}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                              className="text-center"
                            >
                              Name
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.subTitle && (
                            <th className="pt-2 pb-2 text-center">Sub Title</th>
                          )}
                          {visibleColumns.freeSelection && (
                            <th className="pt-2 pb-2 text-center">Free Selection</th>
                          )}
                          {visibleColumns.minSelection && (
                            <th className="pt-2 pb-2 text-center">Minimum Selection</th>
                          )}
                          {visibleColumns.maxSelection && (
                            <th className="pt-2 pb-2 text-center">Maximum Selection</th>
                          )}
                          {visibleColumns.canSkip && (
                            <th className="pt-2 pb-2 text-center">Skip Able</th>
                          )}
                          <th className="pt-2 pb-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedModifierGroups.map((modifier) => (
                          <tr key={modifier.modifierGroupID}>
                            {visibleColumns.name && (
                              <td
                                className="align-middle text-center"
                                onClick={() =>
                                  navigate(`/modifiers/updateModifierGroup/${modifier.modifierGroupID}`)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                {modifier.name}
                              </td>
                            )}
                            {visibleColumns.subTitle && (
                              <td
                                className="align-middle text-center"
                                onClick={() =>
                                  navigate(`/modifiers/updateModifierGroup/${modifier.modifierGroupID}`)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                {modifier.subTitle}
                              </td>
                            )}
                            {visibleColumns.freeSelection && (
                              <td
                                className="align-middle text-center"
                                onClick={() =>
                                  navigate(`/modifiers/updateModifierGroup/${modifier.modifierGroupID}`)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                {modifier.freeSelection}
                              </td>
                            )}
                            {visibleColumns.minSelection && (
                              <td
                                className="align-middle text-center"
                                onClick={() =>
                                  navigate(`/modifiers/updateModifierGroup/${modifier.modifierGroupID}`)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                {modifier.minimumSelection}
                              </td>
                            )}
                            {visibleColumns.maxSelection && (
                              <td
                                className="align-middle text-center"
                                onClick={() =>
                                  navigate(`/modifiers/updateModifierGroup/${modifier.modifierGroupID}`)
                                }
                                style={{ cursor: "pointer" }}
                              >
                                {modifier.maximumSelection}
                              </td>
                            )}
                            {visibleColumns.canSkip && (
                              <td className="align-middle text-center">
                                <StatusBadge
                                  isActive={modifier.canSkip}
                                  activeLabel="Yes"
                                  inactiveLabel="No"
                                  onClick={() => setSkipConfirmModifier(modifier)}
                                />
                              </td>
                            )}
                            <td className="align-middle text-center">
                              <TableActionsMenu menuStyle={{ zIndex: 1050 }}>
                                <TableActionsItem
                                  as={Link}
                                  to={`/modifiers/updateModifierGroup/${modifier.modifierGroupID}`}
                                  icon="fa fa-edit"
                                >
                                  Edit
                                </TableActionsItem>
                                <TableActionsItem
                                  icon="fa fa-plus"
                                  onClick={() => {
                                    setSelectedModifierGroupForAssignment(modifier);
                                    setShowItemAssignmentModal(true);
                                  }}
                                >
                                  Assign to Items
                                </TableActionsItem>
                                <TableActionsItem
                                  danger
                                  icon="fa fa-trash"
                                  onClick={() => handleDelete(modifier)}
                                >
                                  Delete
                                </TableActionsItem>
                              </TableActionsMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>

      <ItemAssignmentModal
        showModal={showItemAssignmentModal}
        setShowModal={setShowItemAssignmentModal}
        modifierGroup={selectedModifierGroupForAssignment}
        compID={CompID}
        onSuccess={() => {
          setShowItemAssignmentModal(false);
          setSelectedModifierGroupForAssignment(null);
        }}
      />

      <ItemAssignmentModal
        showModal={showBulkItemAssignmentModal}
        setShowModal={setShowBulkItemAssignmentModal}
        modifierGroups={Array.from(selectedItems)
          .map((id) =>
            modifierGroups.find((group) => group.modifierGroupID === id)
          )
          .filter(Boolean)}
        compID={CompID}
        isBulk={true}
        onSuccess={() => {
          setShowBulkItemAssignmentModal(false);
          setSelectedItems(new Set());
          lastSelectedIndex.current = null;
        }}
      />

      <CustomModal
        title="Change can skip?"
        subtitle={
          skipConfirmModifier
            ? `Are you sure you want to set "${
                skipConfirmModifier.modifierGroupName ??
                skipConfirmModifier.name ??
                "this modifier group"
              }" can skip to ${skipConfirmModifier.canSkip ? "No" : "Yes"}?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setSkipConfirmModifier(null)}
        isOpen={!!skipConfirmModifier}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmSkipChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
}

export default ManageModifierGroups;